<template>
  <div class="general-section">

    <!-- ── Auto-cierre RDI ── -->
    <div class="section-label">
      <v-icon size="13" color="#2D4B95" class="mr-1">mdi-file-document-outline</v-icon>
      RDI
    </div>

    <div class="closure-toggle-banner" :class="{ active: enabled }">
      <div class="toggle-info">
        <v-icon size="17" :color="enabled ? '#2D4B95' : '#8896b3'" class="mr-2">
          {{ enabled ? 'mdi-clock-check-outline' : 'mdi-clock-off-outline' }}
        </v-icon>
        <div>
          <div class="toggle-title">Cierre automático con respuesta oficial</div>
          <div class="toggle-desc">
            {{ enabled
              ? 'Se cerrará automáticamente tras el tiempo configurado.'
              : 'Usa el flujo de cierre normal de la plataforma.' }}
          </div>
        </div>
      </div>
      <button class="toggle-switch" :class="{ on: enabled }" @click="enabled = !enabled" type="button">
        <span class="toggle-knob" />
      </button>
    </div>

    <transition name="fade-slide">
      <div v-if="enabled" class="config-section">
        <div class="config-label">
          <v-icon size="13" color="#2D4B95" class="mr-1">mdi-timer-outline</v-icon>
          <div>
						<p class="mb-0">Tiempo límite para el cierre</p>
						<p class="mb-0" style="font-size: 9px;opacity: 0.7;">En Horas Hábiles</p>
					</div>
        </div>

        <div class="number-row">
          <button class="num-btn" @click="adjustVal(-1)" type="button"><v-icon size="16">mdi-minus</v-icon></button>
          <input v-model.number="inputVal" type="number" min="1" class="num-input" />
          <button class="num-btn" @click="adjustVal(1)" type="button"><v-icon size="16">mdi-plus</v-icon></button>
        </div>

        <div v-if="previewLabel" class="preview-label">{{ previewLabel }}</div>

        <div class="info-box">
          <v-icon size="14" color="#8896b3" class="mr-1">mdi-information-outline</v-icon>
          <span>Si una RDI <b>CON</b> respuesta oficial no recibe interacciones o actualizaciones dentro de este plazo, se cerrará automáticamente.</span>
        </div>
      </div>
    </transition>
    <!-- ── IA ── -->
    <div class="section-label mt-section">
      <v-icon size="13" color="#2D4B95" class="mr-1">mdi-robot-outline</v-icon>
      IA
    </div>

    <div class="closure-toggle-banner ai-banner" :class="{ active: aiBotAccess }">
      <div class="toggle-top">
        <div class="toggle-info">
          <v-icon size="17" :color="aiBotAccess ? '#2D4B95' : '#8896b3'" class="mr-2">
            {{ aiBotAccess ? 'mdi-robot' : 'mdi-robot-off-outline' }}
          </v-icon>
          <div>
            <div class="toggle-title">Incluir IA en el proyecto</div>
            <div class="toggle-desc">
              {{ aiBotAccess
                ? 'Habilita funcionalidades basadas en IA para este proyecto.'
                : 'Deshabilita funcionalidades basadas en IA.' }}
            </div>
          </div>
        </div>

        <button
          class="toggle-switch"
          :class="{ on: aiBotAccess, disabled: !aiConsent && !aiBotAccess }"
          @click="toggleAI"
          :disabled="!aiConsent && !aiBotAccess"
          type="button"
        >
          <span class="toggle-knob" />
        </button>
      </div>

      <div class="info-box danger-box ai-warning">
        <v-icon size="14" color="#dc2626" class="mr-1">mdi-alert-outline</v-icon>
        <span>Al activar IA, algunos datos e interacciones del proyecto podrían enviarse a proveedores externos (por ejemplo, OpenAI) para su análisis y generación de respuestas.</span>
      </div>

      <div class="consent-row">
        <input id="ai-consent" type="checkbox" v-model="aiConsent" class="consent-checkbox" />
        <label for="ai-consent" class="consent-label">He leído y acepto que algunos datos e interacciones del proyecto podrían enviarse a proveedores externos (por ejemplo, OpenAI) para su análisis y generación de respuestas.</label>
      </div>
    </div>

    <!-- ── Horario laboral ── -->
    <div class="section-label mt-section">
      <v-icon size="13" color="#2D4B95" class="mr-1">mdi-clock-outline</v-icon>
      Horario laboral
    </div>

    <div class="schedule-desc">
      Configura los días y horarios en que se contabilizan las horas hábiles.
      Los días no seleccionados no cuentan como tiempo hábil.
    </div>

    <div class="days-row">
      <button
        v-for="d in weekDays"
        :key="d.value"
        class="day-btn"
        :class="{ active: isDayActive(d.value) }"
        @click="toggleDay(d.value)"
        type="button"
      >{{ d.label }}</button>
    </div>

    <transition-group name="fade-slide" tag="div" class="schedule-entries">
      <div
        v-for="entry in sortedSchedule"
        :key="entry.day"
        class="schedule-entry"
      >
        <div class="entry-day-label">{{ dayName(entry.day) }}</div>
        <div class="entry-times">
          <div class="time-field">
            <label class="time-label">Entrada</label>
            <div class="time-input-custom">
              <input
                type="number"
                class="time-num"
                min="0" max="23"
                :value="getHour(entry.start_working_hour)"
                @change="updateHour(entry.day, 'start_working_hour', $event.target.value)"
              />
              <span class="time-colon">:</span>
              <input
                type="number"
                class="time-num"
                min="0" max="59" step="5"
                :value="getMinute(entry.start_working_hour)"
                @change="updateMinute(entry.day, 'start_working_hour', $event.target.value)"
              />
            </div>
          </div>
          <v-icon size="14" color="#b0bedd" class="time-separator">mdi-arrow-right</v-icon>
          <div class="time-field">
            <label class="time-label">Salida</label>
            <div class="time-input-custom">
              <input
                type="number"
                class="time-num"
                min="0" max="23"
                :value="getHour(entry.end_working_hour)"
                @change="updateHour(entry.day, 'end_working_hour', $event.target.value)"
              />
              <span class="time-colon">:</span>
              <input
                type="number"
                class="time-num"
                min="0" max="59" step="5"
                :value="getMinute(entry.end_working_hour)"
                @change="updateMinute(entry.day, 'end_working_hour', $event.target.value)"
              />
            </div>
          </div>
        </div>
      </div>
    </transition-group>

  </div>
</template>

<script>
import { setProjectConfig } from '@/helpers/api/project'

export default {
  name: 'GeneralSection',

  props: {
    project: { type: Object, required: true }
  },

  data() {
    return {
      // Auto-cierre
      enabled: false,
      inputVal: 72,
        // IA
        aiBotAccess: false,
        aiConsent: false,

      // Horario laboral
      workSchedule: [],

      weekDays: [
        { value: 1, label: 'L' },
        { value: 2, label: 'M' },
        { value: 3, label: 'X' },
        { value: 4, label: 'J' },
        { value: 5, label: 'V' },
        { value: 6, label: 'S' },
        { value: 0, label: 'D' },
      ]
    }
  },

  computed: {
    hoursValue() {
      if (!this.enabled || !this.inputVal) return null
      return this.inputVal
    },

    previewLabel() {
      if (!this.inputVal || this.inputVal < 1) return ''
      const limitHours = this.inputVal

      // Sin horario configurado: sin preview
      if (!this.workSchedule.length) return ''

      // Con horario: calcular cuántos días/semanas del calendario laboral representa
      const order = [1, 2, 3, 4, 5, 6, 0]
      const sorted = [...this.workSchedule].sort((a, b) => order.indexOf(a.day) - order.indexOf(b.day))
      const dayHoursArr = sorted.map(e => {
        const [sh, sm] = e.start_working_hour.split(':').map(Number)
        const [eh, em] = e.end_working_hour.split(':').map(Number)
        return Math.max(0, (eh + em / 60) - (sh + sm / 60))
      })
      const totalWeekHours = dayHoursArr.reduce((s, h) => s + h, 0)
      if (totalWeekHours === 0) return ''

      const firstDayName = this.dayName(sorted[0].day)

      let remaining = limitHours
      const weeks = Math.floor(remaining / totalWeekHours)
      remaining -= weeks * totalWeekHours

      let fullDays = 0
      let partialHours = 0
      for (let i = 0; i < dayHoursArr.length; i++) {
        if (remaining === 0) break
        if (remaining >= dayHoursArr[i]) {
          remaining -= dayHoursArr[i]
          fullDays++
        } else {
          partialHours = Math.round(remaining * 10) / 10
          break
        }
      }

      const parts = []
      if (weeks > 0) parts.push(`${weeks} ${weeks === 1 ? 'semana' : 'semanas'}`)
      if (fullDays > 0) parts.push(`${fullDays} ${fullDays === 1 ? 'día' : 'días'}`)
      if (partialHours > 0) parts.push(`${partialHours} h`)

      const calText = parts.length ? parts.join(', ') : '< 1 h'
      return `≈ ${calText} según horario configurado (considerado desde ${firstDayName})`
    },

    sortedSchedule() {
      const order = [1, 2, 3, 4, 5, 6, 0]
      return [...this.workSchedule].sort((a, b) => order.indexOf(a.day) - order.indexOf(b.day))
    },

    isValid() {
      if (this.enabled && (!this.inputVal || this.inputVal < 1)) return false
      for (const e of this.workSchedule) {
        if (!e.start_working_hour || !e.end_working_hour) return false
        if (e.start_working_hour >= e.end_working_hour) return false
      }
      return true
    }
  },

  methods: {
    loadConfiguration() {
      const config = this.project.project_config
      // Auto-cierre
      const limit = config?.rdi_approval_hours_limit
      if (limit) {
        this.enabled = true
        this.inputVal = limit
      } else {
        this.enabled = false
        this.inputVal = 72
      }
      // Horario laboral
      this.workSchedule = config?.work_schedule ? JSON.parse(JSON.stringify(config.work_schedule)) : []
      // IA
      this.aiBotAccess = !!config?.ai_bot_access
      // Si IA ya está habilitada, asumimos consentimiento previo
      this.aiConsent = !!config?.ai_bot_access
    },

    adjustVal(delta) {
      this.inputVal = Math.max(1, (this.inputVal || 0) + delta)
    },

    isDayActive(day) {
      return this.workSchedule.some(e => e.day === day)
    },

    toggleDay(day) {
      const idx = this.workSchedule.findIndex(e => e.day === day)
      if (idx === -1) {
        this.workSchedule.push({ day, start_working_hour: '08:00', end_working_hour: '18:00' })
      } else {
        this.workSchedule.splice(idx, 1)
      }
    },

    updateTime(day, field, value) {
      const entry = this.workSchedule.find(e => e.day === day)
      if (entry) entry[field] = value
    },

    getHour(timeStr) {
      if (!timeStr) return 8
      return parseInt(timeStr.split(':')[0], 10)
    },
    getMinute(timeStr) {
      if (!timeStr) return 0
      return parseInt(timeStr.split(':')[1], 10)
    },
    updateHour(day, field, val) {
      const h = Math.min(23, Math.max(0, parseInt(val, 10) || 0))
      const entry = this.workSchedule.find(e => e.day === day)
      if (entry) {
        const m = entry[field] ? entry[field].split(':')[1] : '00'
        entry[field] = `${String(h).padStart(2, '0')}:${m}`
      }
    },
    updateMinute(day, field, val) {
      const m = Math.min(59, Math.max(0, parseInt(val, 10) || 0))
      const entry = this.workSchedule.find(e => e.day === day)
      if (entry) {
        const h = entry[field] ? entry[field].split(':')[0] : '08'
        entry[field] = `${h}:${String(m).padStart(2, '0')}`
      }
    },

    dayName(day) {
      const names = { 0: 'Domingo', 1: 'Lunes', 2: 'Martes', 3: 'Miércoles', 4: 'Jueves', 5: 'Viernes', 6: 'Sábado' }
      return names[day] || day
    },

    toggleAI() {
      // Si se intenta activar IA, requerir consentimiento explícito
      if (!this.aiBotAccess && !this.aiConsent) {
        return
      }
      this.aiBotAccess = !this.aiBotAccess
    },

    async save() {
      await setProjectConfig({
        project_id: this.project.id,
        rdi_approval_hours_limit: this.hoursValue,
        work_schedule: this.workSchedule.length ? this.workSchedule : null,
        ai_bot_access: this.aiBotAccess
      })
    }
  }
}
</script>

<style scoped>
* { box-sizing: border-box; }

.general-section { padding: 0; }

.section-label {
  font-size: 11px; font-weight: 600; color: #2D4B95;
  text-transform: uppercase; letter-spacing: 0.07em;
  display: flex; align-items: center; margin-bottom: 10px;
}
.mt-section { margin-top: 22px; }

/* Toggle banner */
.closure-toggle-banner {
  display: flex; align-items: center; justify-content: space-between;
  padding: 13px 15px; border-radius: 12px;
  border: 1.5px solid #e2e8f4; background: #f7f9fc;
  margin-bottom: 16px; transition: all 0.25s ease;
}
.closure-toggle-banner.active { border-color: rgba(45,75,149,0.4); background: #eef2fb; }
.toggle-info { display: flex; align-items: center; }
.toggle-title { font-size: 13px; font-weight: 600; color: #2d3748; line-height: 1.3; }
.toggle-desc { font-size: 11.5px; color: #8896b3; margin-top: 2px; }
.toggle-switch {
  flex-shrink: 0; width: 40px; height: 23px; border-radius: 12px;
  border: none; background: #d1d9e8; cursor: pointer;
  position: relative; transition: background 0.22s ease; padding: 0;
}
.toggle-switch.on { background: #2D4B95; }
.toggle-knob {
  position: absolute; top: 3px; left: 3px;
  width: 17px; height: 17px; border-radius: 50%;
  background: white; box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  transition: transform 0.22s cubic-bezier(.34,1.56,.64,1); display: block;
}
.toggle-switch.on .toggle-knob { transform: translateX(17px); }

/* Config section */
.config-section { padding-left: 4px; margin-bottom: 4px; }
.config-label {
  font-size: 11px; font-weight: 600; color: #2D4B95;
  text-transform: uppercase; letter-spacing: 0.07em;
  display: flex; align-items: center; margin-bottom: 10px;
}
.number-row { display: flex; align-items: center; gap: 10px; margin-bottom: 6px; }
.num-btn {
  width: 36px; height: 36px; border-radius: 8px;
  border: 1.5px solid #e2e8f4; background: transparent;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; color: #2d3748; transition: all 0.15s;
}
.num-btn:hover { border-color: #2D4B95; background: #eef2fb; }
.num-input {
  flex: 1; text-align: center; font-size: 22px; font-weight: 600;
  border: 1.5px solid #e2e8f4; border-radius: 8px;
  padding: 8px; color: #2d3748; outline: none; transition: border-color 0.15s;
}
.num-input:focus { border-color: #2D4B95; }
.num-input::-webkit-inner-spin-button,
.num-input::-webkit-outer-spin-button { -webkit-appearance: none; }
.num-unit { font-size: 12px; font-weight: 600; color: #8896b3; white-space: nowrap; }
.preview-label { text-align: center; font-size: 12px; color: #8896b3; margin-bottom: 12px; }
.info-box {
  background: #f7f9fc; border-radius: 8px;
  padding: 10px 13px; border: 0.5px solid #e2e8f4;
  font-size: 12px; color: #8896b3; line-height: 1.55;
  display: flex; align-items: flex-start; margin-bottom: 4px;
}

/* Horario laboral */
.schedule-desc {
  font-size: 12.5px; color: #8896b3; margin-bottom: 14px; line-height: 1.5;
}
.days-row { display: flex; gap: 6px; margin-bottom: 16px; flex-wrap: wrap; }
.day-btn {
  width: 34px; height: 34px; border-radius: 50%;
  border: 1.5px solid #e2e8f4; background: transparent;
  font-size: 12px; font-weight: 600; color: #8896b3;
  cursor: pointer; transition: all 0.15s;
  display: flex; align-items: center; justify-content: center;
}
.day-btn.active { border-color: #2D4B95; background: #2D4B95; color: #fff; }
.day-btn:hover:not(.active) { border-color: #2D4B95; color: #2D4B95; background: #eef2fb; }

.schedule-entries { display: flex; flex-direction: column; gap: 8px; }
.schedule-entry {
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 14px; border-radius: 10px;
  border: 1.5px solid #e2e8f4; background: #f7f9fc;
}
.entry-day-label { font-size: 13px; font-weight: 600; color: #2d3748; min-width: 80px; }
.entry-times { display: flex; align-items: center; gap: 8px; }
.time-field { display: flex; flex-direction: column; align-items: center; gap: 3px; }
.time-label { font-size: 10px; font-weight: 600; color: #8896b3; text-transform: uppercase; letter-spacing: 0.05em; }
.time-input-custom {
  display: flex; align-items: center; gap: 2px;
  border: 1.5px solid #e2e8f4; border-radius: 7px;
  background: #fff; padding: 4px 6px; transition: border-color 0.15s;
}
.time-input-custom:focus-within { border-color: #2D4B95; }
.time-num {
  width: 36px; text-align: center; font-size: 13px; font-weight: 600;
  color: #2d3748; border: none; outline: none; background: transparent;
  -moz-appearance: textfield;
}
.time-num::-webkit-inner-spin-button,
.time-num::-webkit-outer-spin-button { -webkit-appearance: none; }
.time-colon { font-size: 14px; font-weight: 700; color: #8896b3; line-height: 1; }
.time-separator { flex-shrink: 0; }

.fade-slide-enter-active, .fade-slide-leave-active { transition: all 0.2s ease; }
.fade-slide-enter, .fade-slide-leave-to { opacity: 0; transform: translateY(-6px); }

/* AI banner specific styling */
.ai-banner { flex-direction: column; align-items: stretch; padding-bottom: 10px; }
.ai-banner .toggle-top { display: flex; align-items: center; justify-content: space-between; width: 100%; }
.ai-warning { margin-top: 8px; }
.danger-box {
  background: #fff1f2; /* light red */
  border: 0.5px solid #fecaca;
  color: #7f1d1d;
}
.danger-box v-icon { color: #dc2626; }
/* Consent checkbox styling */
.ai-banner .consent-row { display: flex; align-items: flex-start; gap: 10px; margin-top: 8px; }
.consent-checkbox { width: 16px; height: 16px; margin-top: 4px; }
.consent-label { font-size: 12px; color: #7f1d1d; line-height: 1.3; }
.toggle-switch[disabled], .toggle-switch.disabled { opacity: 0.45; cursor: not-allowed; pointer-events: none; }
</style>
